import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Clock, User, Calendar } from 'lucide-react';
import { PolicyReader } from '@/components/policy-reader/PolicyReader';
import { Policy } from '@/types/policy';
import { PolicyReadingSession, QuizAttempt } from '@/types/policy-reader';
import { getPolicy, getDocPolicies } from '@/services/policyService';
import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { formatDateTime } from '@/utils/time-utils';

export const PolicyReaderPage: React.FC = () => {
  const { policyId } = useParams<{ policyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();

  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readingSession, setReadingSession] = useState<PolicyReadingSession | null>(null);
  const [quizAttempt, setQuizAttempt] = useState<QuizAttempt | null>(null);

  useEffect(() => {
    const loadPolicy = async () => {
      if (!policyId) {
        setError('Policy ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // First try to get regular policy
        let policyData = await getPolicy(policyId);

        // If not found, try document library policies
        if (!policyData && activeTenancy?.id) {
          console.log('Regular policy not found, trying document library policies...');
          const userIdToName = (userId: string) => userId; // Simple fallback function
          const docPolicies = await getDocPolicies(activeTenancy.id, userIdToName);
          const foundDocPolicy = docPolicies.find(p =>
            p.id === policyId ||
            (p as any).policy_id === policyId ||
            (p as any).componentID === policyId
          );

          // Convert PolicyListItem to Policy format for compatibility
          if (foundDocPolicy) {
            policyData = foundDocPolicy as any; // Type assertion for compatibility
          }
        }

        if (!policyData) {
          throw new Error('Policy not found');
        }

        console.log('Loaded policy:', policyData);
        setPolicy(policyData);
      } catch (err) {
        console.error('Failed to load policy:', err);
        setError(err instanceof Error ? err.message : 'Failed to load policy');
      } finally {
        setLoading(false);
      }
    };

    loadPolicy();
  }, [policyId]);

  const handleReadingComplete = (session: PolicyReadingSession) => {
    setReadingSession(session);
    console.log('Reading session completed:', session);

    // Here you could save the reading session to the database
    // await saveReadingSession(session);
  };

  const handleQuizComplete = (attempt: QuizAttempt) => {
    setQuizAttempt(attempt);
    console.log('Quiz completed:', attempt);

    // Here you could save the quiz attempt to the database
    // await saveQuizAttempt(attempt);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
            <p className="text-muted-foreground">Loading policy...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={handleGoBack} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!policy || !policyId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-muted-foreground mb-4">Policy not found</p>
              <Button onClick={handleGoBack} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <Button onClick={handleGoBack} variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/*<Card>*/}
        {/*  <CardHeader>*/}
        {/*    <div className="flex items-start justify-between">*/}
        {/*      <div className="space-y-2">*/}
        {/*        <CardTitle className="text-xl">{policy.policy_name}</CardTitle>*/}
        {/*        <div className="flex items-center space-x-3 text-sm text-muted-foreground">*/}
        {/*          <div className="flex items-center space-x-1">*/}
        {/*            <FileText className="h-4 w-4" />*/}
        {/*            <span>Version {policy.policy_version}</span>*/}
        {/*          </div>*/}
        {/*          <Badge variant="outline">{policy.policy_status}</Badge>*/}
        {/*          {policy.policyCategory && (*/}
        {/*            <Badge variant="secondary">{policy.policyCategory}</Badge>*/}
        {/*          )}*/}
        {/*        </div>*/}
        {/*        {policy.policy_history?.last_reviewed && (*/}
        {/*          <div className="flex items-center space-x-1 text-sm text-muted-foreground">*/}
        {/*            <Clock className="h-4 w-4" />*/}
        {/*            <span>Last reviewed: {formatDateTime(policy.policy_history.last_reviewed)}</span>*/}
        {/*          </div>*/}
        {/*        )}*/}
        {/*      </div>*/}
        {/*      */}
        {/*      <div className="text-right space-y-2">*/}
        {/*        {readingSession && (*/}
        {/*          <Badge variant="default" className="bg-green-500">*/}
        {/*            Reading Complete*/}
        {/*          </Badge>*/}
        {/*        )}*/}
        {/*        {quizAttempt && (*/}
        {/*          <Badge */}
        {/*            variant={quizAttempt.passed ? "default" : "secondary"}*/}
        {/*            className={quizAttempt.passed ? "bg-blue-500" : ""}*/}
        {/*          >*/}
        {/*            Quiz {quizAttempt.passed ? 'Passed' : 'Completed'} ({Math.round(quizAttempt.score)}%)*/}
        {/*          </Badge>*/}
        {/*        )}*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </CardHeader>*/}
        {/*</Card>*/}
      </div>

      {/* Policy Reader */}
      <PolicyReader
        policyId={policyId}
        policy={policy}
        onComplete={handleReadingComplete}
        onQuizComplete={handleQuizComplete}
      />

      {/* Completion Summary */}
      {(readingSession || quizAttempt) && false && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Session Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {readingSession && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Reading Session</h4>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>Started: {formatDateTime(readingSession.startTime)}</p>
                    {readingSession.endTime && (
                      <p>Completed: {formatDateTime(readingSession.endTime)}</p>
                    )}
                    <p>Total Reading Time: {Math.round(readingSession.metrics.totalReadTime / 1000 / 60)} minutes</p>
                    <p>Active Reading Time: {Math.round(readingSession.metrics.totalDwellTime / 1000 / 60)} minutes</p>
                    <p>Pages Read: {readingSession.metrics.pageMetrics.filter(p => p.viewCount > 0).length}</p>
                  </div>
                </div>
              )}

              {quizAttempt && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Quiz Results</h4>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>Score: {Math.round(quizAttempt.score)}%</p>
                    <p>Status: {quizAttempt.passed ? 'Passed' : 'Needs Review'}</p>
                    <p>Time Spent: {Math.round(quizAttempt.timeSpent / 1000 / 60)} minutes</p>
                    <p>Completed: {formatDateTime(quizAttempt.endTime || new Date())}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PolicyReaderPage;
